import {
  AMOUNT_FONTS,
  BREADCRUMB_STYLES,
  CHAT_PLACEMENTS,
  CHAT_ROW_STYLES,
  CHROME_STYLES,
  CONTROL_SHAPES,
  CONTROL_STYLES,
  DENSITIES,
  DISPLAY_CASES,
  DISPLAY_TRACKINGS,
  DISPLAY_WEIGHTS,
  FOOTER_STYLES,
  GLASS_STRENGTHS,
  GLOW_INTENSITIES,
  HEADER_STYLES,
  ICON_MOTIONS,
  ICON_STYLES,
  LABEL_CASES,
  lootThemeInputSchema,
  MAX_STAGE_COLORS,
  MOTION_PRESETS,
  NAV_LINK_STYLES,
  PAGE_MARGIN_PX,
  resolveThemeSpec,
  SEMANTIC_COLOR_KEYS,
  type SemanticColorKey,
  STAGE_MOTIONS,
  STAGE_RECIPES,
  STOREFRONT_RARITY_TIERS,
  STOREFRONT_TOKEN_GROUPS,
  type StorefrontTheme,
  SURFACE_STYLES,
  TEXT_WEIGHTS,
} from "@loot-os/api-contract";

import { DEFAULT_RADIUS_PX, radiusToPx, setRarityOverrides, setTokenOverrides } from "./storefront";

/**
 * The groups of a theme whose fields are each one pick from a fixed set (`themeSpec.ts`), which
 * the Theme tab draws as one choice control per field.
 */
export type ThemeChoiceGroup =
  | "type"
  | "shape"
  | "surface"
  | "glow"
  | "motion"
  | "icons"
  | "stage"
  | "layout";

/** One option of a choice: the stored value and the words the editor shows for it. */
export interface ThemeChoiceOption<V extends string | number = string | number> {
  value: V;
  label: string;
}

/** One field of a choice group, as the editor draws it. */
export interface ThemeChoice {
  group: ThemeChoiceGroup;
  /** The field's key inside its group (`shape.controls` → `controls`). */
  field: string;
  label: string;
  options: readonly ThemeChoiceOption[];
}

function options<V extends string | number>(
  values: readonly V[],
  labels: Record<V, string>,
): ThemeChoiceOption<V>[] {
  return values.map((value) => ({ value, label: labels[value] }));
}

const BUTTON_SHAPE: ThemeChoice = {
  group: "shape",
  field: "controls",
  label: "Buttons and inputs",
  options: options(CONTROL_SHAPES, { rounded: "Rounded", pill: "Pill" }),
};
const CONTROL_STYLE: ThemeChoice = {
  group: "shape",
  field: "controlStyle",
  label: "Controls",
  options: options(CONTROL_STYLES, { outlined: "Outlined", filled: "Filled" }),
};
const DENSITY: ThemeChoice = {
  group: "shape",
  field: "density",
  label: "Density",
  options: options(DENSITIES, { compact: "Compact", default: "Default", roomy: "Roomy" }),
};
const CARD_STYLE: ThemeChoice = {
  group: "surface",
  field: "style",
  label: "Cards",
  options: options(SURFACE_STYLES, { flat: "Flat", soft: "Soft" }),
};
const GLASS: ThemeChoice = {
  group: "surface",
  field: "glass",
  label: "Glass",
  options: options(GLASS_STRENGTHS, { 0: "Off", 1: "Light", 2: "Medium", 3: "Strong" }),
};
const HEADING_WEIGHT: ThemeChoice = {
  group: "type",
  field: "displayWeight",
  label: "Weight",
  options: options(DISPLAY_WEIGHTS, {
    500: "Medium",
    600: "Semibold",
    700: "Bold",
    800: "Extra bold",
  }),
};
const HEADING_SPACING: ThemeChoice = {
  group: "type",
  field: "displayTracking",
  label: "Letter spacing",
  options: options(DISPLAY_TRACKINGS, { tight: "Tight", normal: "Normal" }),
};
const HEADING_CASE: ThemeChoice = {
  group: "type",
  field: "displayCase",
  label: "Case",
  options: options(DISPLAY_CASES, { none: "As written", upper: "Uppercase" }),
};
const LABEL_CASE: ThemeChoice = {
  group: "type",
  field: "labelCase",
  label: "Button labels",
  options: options(LABEL_CASES, { none: "As written", upper: "Uppercase" }),
};
const TEXT_WEIGHT: ThemeChoice = {
  group: "type",
  field: "textWeight",
  label: "Emphasis",
  options: options(TEXT_WEIGHTS, { light: "Light", medium: "Medium", heavy: "Heavy" }),
};
const AMOUNT_FONT: ThemeChoice = {
  group: "type",
  field: "amountFont",
  label: "Prices",
  options: options(AMOUNT_FONTS, { mono: "Mono font", body: "Body font" }),
};
const MOTION: ThemeChoice = {
  group: "motion",
  field: "preset",
  label: "Motion",
  options: options(MOTION_PRESETS, { calm: "Calm", standard: "Standard", lively: "Lively" }),
};
const GLOW: ThemeChoice = {
  group: "glow",
  field: "intensity",
  label: "Glow",
  options: options(GLOW_INTENSITIES, { 0: "Off", 1: "Subtle", 2: "Medium", 3: "Strong" }),
};
const ICON_ANIMATION: ThemeChoice = {
  group: "icons",
  field: "motion",
  label: "Icon animation",
  options: options(ICON_MOTIONS, { none: "None", events: "Feedback only", all: "All" }),
};
const ICON_STYLE: ThemeChoice = {
  group: "icons",
  field: "style",
  label: "Icon style",
  options: options(ICON_STYLES, {
    rounded: "Rounded",
    sharp: "Sharp",
    outline: "Outline",
    duotone: "Duotone",
  }),
};
const BACKGROUND_EFFECT: ThemeChoice = {
  group: "stage",
  field: "recipe",
  label: "Background effect",
  options: options(STAGE_RECIPES, { none: "None", glow: "Glow", water: "Water", aurora: "Aurora" }),
};

const STAGE_MOTION: ThemeChoice = {
  group: "stage",
  field: "motion",
  label: "Movement",
  options: options(STAGE_MOTIONS, { still: "Still", alive: "Alive" }),
};

/**
 * One option of a Style choice: a word for a combination of fields ("Round": the default radius
 * and pill buttons). It sets those fields and no others, so picking one keeps the rest.
 */
export interface ThemeStylePreset {
  label: string;
  /** The fields it sets, by `group.field`. */
  values: Readonly<Record<string, string | number>>;
  /** The corner radius in px it sets, where the choice carries the radius. */
  radiusPx?: number;
}

/**
 * One of the Theme tab's Style choices: a decision a tenant makes in a word, whose options each
 * set several fields together. Details shows those fields one by one; a theme whose fields match
 * no option shows as Custom.
 */
export interface ThemeStyle {
  id: "corners" | "surfaces" | "headings" | "text" | "energy" | "icons" | "backdrop";
  /** The Theme tab's card it sits in: Type (beside the fonts) or Style. */
  card: "type" | "style";
  title: string;
  description: string;
  presets: readonly ThemeStylePreset[];
  /** The fields Details draws, each one control. */
  fields: readonly ThemeChoice[];
  /** Details also carries the corner radius. */
  radius?: boolean;
  /** Details also carries the background effect's colors. */
  stageColors?: boolean;
}

/** The radius the storefront draws when the theme sets none. */
const SOFT_RADIUS_PX = DEFAULT_RADIUS_PX;

/**
 * The Style choices of the Theme tab, in its order. Every look field of `themeSpecShape` is a
 * field of exactly one (a test holds it); the site frame's fields (`layout`) are the Pages tab's.
 */
export const THEME_STYLES: readonly ThemeStyle[] = [
  {
    id: "corners",
    card: "style",
    title: "Corners",
    description: "How round cards, panels and buttons are, and how much room controls take.",
    presets: [
      { label: "Sharp", values: { "shape.controls": "rounded" }, radiusPx: 4 },
      { label: "Soft", values: { "shape.controls": "rounded" }, radiusPx: SOFT_RADIUS_PX },
      { label: "Round", values: { "shape.controls": "pill" }, radiusPx: SOFT_RADIUS_PX },
    ],
    fields: [BUTTON_SHAPE, CONTROL_STYLE, DENSITY],
    radius: true,
  },
  {
    id: "surfaces",
    card: "style",
    title: "Surfaces",
    description:
      "Flat cards carry a border, soft ones a faint top highlight; glass blurs what is behind floating panels.",
    presets: [
      { label: "Flat", values: { "surface.style": "flat", "surface.glass": 0 } },
      { label: "Soft", values: { "surface.style": "soft", "surface.glass": 0 } },
      { label: "Glass", values: { "surface.style": "soft", "surface.glass": 3 } },
    ],
    fields: [CARD_STYLE, GLASS],
  },
  {
    id: "headings",
    card: "type",
    title: "Heading style",
    description:
      "The weight, letter spacing and case of page titles and display text, and the case of button labels.",
    presets: [
      {
        label: "Quiet",
        values: {
          "type.displayWeight": 600,
          "type.displayTracking": "normal",
          "type.displayCase": "none",
          "type.labelCase": "none",
        },
      },
      {
        label: "Bold",
        values: {
          "type.displayWeight": 700,
          "type.displayTracking": "tight",
          "type.displayCase": "none",
          "type.labelCase": "none",
        },
      },
      {
        label: "Loud",
        values: {
          "type.displayWeight": 800,
          "type.displayTracking": "tight",
          "type.displayCase": "upper",
          "type.labelCase": "upper",
        },
      },
    ],
    fields: [HEADING_WEIGHT, HEADING_SPACING, HEADING_CASE, LABEL_CASE],
  },
  {
    id: "text",
    card: "type",
    title: "Text weight",
    description:
      "How heavy labels, names and prices read across the store, from airy to bold, and the font prices are set in.",
    presets: [
      { label: "Light", values: { "type.textWeight": "light", "type.amountFont": "mono" } },
      { label: "Medium", values: { "type.textWeight": "medium", "type.amountFont": "mono" } },
      { label: "Heavy", values: { "type.textWeight": "heavy", "type.amountFont": "body" } },
    ],
    fields: [TEXT_WEIGHT, AMOUNT_FONT],
  },
  {
    id: "energy",
    card: "style",
    title: "Energy",
    description:
      "How lively the store feels: the pace of motion, how strongly rare items and the accent glow, and when icons animate.",
    presets: [
      {
        label: "Calm",
        values: { "motion.preset": "calm", "glow.intensity": 1, "icons.motion": "events" },
      },
      {
        label: "Standard",
        values: { "motion.preset": "standard", "glow.intensity": 1, "icons.motion": "events" },
      },
      {
        label: "Lively",
        values: { "motion.preset": "lively", "glow.intensity": 2, "icons.motion": "all" },
      },
    ],
    fields: [MOTION, GLOW, ICON_ANIMATION],
  },
  {
    id: "icons",
    card: "style",
    title: "Icons",
    description: "How the store's icons are drawn.",
    presets: ICON_STYLE.options.map((option) => ({
      label: option.label,
      values: { "icons.style": option.value },
    })),
    fields: [],
  },
  {
    id: "backdrop",
    card: "style",
    title: "Backdrop",
    description: "An animated background behind the page, drawn in up to four colors.",
    presets: BACKGROUND_EFFECT.options.map((option) => ({
      label: option.label,
      values: { "stage.recipe": option.value },
    })),
    fields: [STAGE_MOTION],
    stageColors: true,
  },
];

/** The Icons and Backdrop choices draw their one field as their options, not under Details. */
const STYLE_OPTION_FIELDS: readonly ThemeChoice[] = [ICON_STYLE, BACKGROUND_EFFECT];

/**
 * The site frame's fields, which the Pages tab draws (Header and footer, which also holds the
 * breadcrumbs; Chat): the theme stores
 * them beside the look, so a reference theme carries its frame, but they are how the store is
 * put together, not how it looks.
 */
export const SITE_FRAME_CHOICES = {
  header: {
    group: "layout",
    field: "header",
    label: "Header",
    options: options(HEADER_STYLES, { attached: "Attached", floating: "Floating" }),
  },
  chrome: {
    group: "layout",
    field: "chrome",
    label: "Header fill",
    options: options(CHROME_STYLES, { solid: "Solid", glass: "Glass" }),
  },
  navLinks: {
    group: "layout",
    field: "navLinks",
    label: "Header links",
    options: options(NAV_LINK_STYLES, { text: "Text", icon: "Icon and text" }),
  },
  footer: {
    group: "layout",
    field: "footer",
    label: "Footer",
    options: options(FOOTER_STYLES, { plain: "Plain", tear: "Pack to rip" }),
  },
  breadcrumb: {
    group: "layout",
    field: "breadcrumb",
    label: "Breadcrumbs",
    options: options(BREADCRUMB_STYLES, { path: "Full path", back: "Back link", none: "None" }),
  },
  pageMargin: {
    group: "layout",
    field: "pageMargin",
    label: "Page margins",
    // Presets; the Pages tab's slider sets any whole px between PAGE_MARGIN_PX's bounds.
    options: [
      { value: 32, label: "Narrow" },
      { value: 56, label: "Medium" },
      { value: PAGE_MARGIN_PX.default, label: "Wide" },
    ],
  },
  chatRows: {
    group: "layout",
    field: "chatRows",
    label: "Chat messages",
    options: options(CHAT_ROW_STYLES, { card: "Cards", stream: "Lines" }),
  },
  chat: {
    group: "layout",
    field: "chat",
    label: "Chat placement",
    options: options(CHAT_PLACEMENTS, {
      drawer: "Drawer",
      "dock-left": "Docked left",
      "dock-right": "Docked right",
    }),
  },
} as const satisfies Record<string, ThemeChoice>;

/** Every choice field the editors draw: the Style choices' and the site frame's. */
export const THEME_CHOICES: readonly ThemeChoice[] = [
  ...THEME_STYLES.flatMap((style) => style.fields),
  ...STYLE_OPTION_FIELDS,
  ...Object.values(SITE_FRAME_CHOICES),
];

/** The choice field a preset key (`group.field`) names. */
function choiceAt(path: string): ThemeChoice {
  const choice = THEME_CHOICES.find((entry) => `${entry.group}.${entry.field}` === path);
  if (!choice) throw new Error(`No theme choice at ${path}.`);
  return choice;
}

/** The radius the storefront draws for `theme`, in px. */
function radiusPxOf(theme: StorefrontTheme): number {
  return radiusToPx(theme.radius ?? "") ?? DEFAULT_RADIUS_PX;
}

/** The option of `style` that `theme` matches, or null for Custom. */
export function matchedPreset(theme: StorefrontTheme, style: ThemeStyle): ThemeStylePreset | null {
  return (
    style.presets.find(
      (preset) =>
        Object.entries(preset.values).every(
          ([path, value]) => choiceValue(theme, choiceAt(path)) === value,
        ) &&
        (preset.radiusPx === undefined || radiusPxOf(theme) === preset.radiusPx),
    ) ?? null
  );
}

/**
 * `theme` with a Style option picked: the fields it names that the store doesn't already draw
 * that way are set, everything else kept. A field already right is left as it is (unset stays
 * unset), and the default radius is stored as unset, so picking what the store already looks
 * like is no change.
 */
export function withPreset(theme: StorefrontTheme, preset: ThemeStylePreset): StorefrontTheme {
  let next = theme;
  for (const [path, value] of Object.entries(preset.values)) {
    const choice = choiceAt(path);
    if (choiceValue(next, choice) !== value) next = withChoice(next, choice, value);
  }
  if (preset.radiusPx !== undefined && radiusPxOf(next) !== preset.radiusPx) {
    next = { ...next, radius: preset.radiusPx === DEFAULT_RADIUS_PX ? "" : `${preset.radiusPx}px` };
  }
  return next;
}

/** What a Style choice's fields are set to, for its line under the title ("Motion: Calm · …"). */
export function styleSummary(theme: StorefrontTheme, style: ThemeStyle): string {
  const parts: string[] = [];
  if (style.radius) parts.push(`Radius: ${radiusPxOf(theme)}px`);
  for (const choice of style.fields) {
    const value = choiceValue(theme, choice);
    const option = choice.options.find((entry) => entry.value === value);
    if (option) parts.push(`${choice.label}: ${option.label}`);
  }
  return parts.join(" · ");
}

/** Every leaf of a saved theme as `path → value`, arrays as one value. */
function themeLeaves(value: unknown, path: string, into: Map<string, string>): Map<string, string> {
  if (value && typeof value === "object" && !Array.isArray(value)) {
    for (const [key, entry] of Object.entries(value)) {
      themeLeaves(entry, path ? `${path}.${key}` : key, into);
    }
  } else if (value !== undefined) {
    into.set(path, JSON.stringify(value));
  }
  return into;
}

/**
 * How many fields a save of `draft` would change against the stored theme, compared as both
 * would be saved, so a field typed and cleared again, or a blank, is no change.
 */
export function countThemeChanges(stored: StorefrontTheme, draft: StorefrontTheme): number {
  const before = themeLeaves(toSavedTheme(stored), "", new Map());
  const after = themeLeaves(toSavedTheme(draft), "", new Map());
  let changes = 0;
  for (const key of new Set([...before.keys(), ...after.keys()])) {
    if (before.get(key) !== after.get(key)) changes += 1;
  }
  return changes;
}

/** One field a save would change, as the editor's review lists it. */
export interface ThemeChange {
  /** `group.field`, or the field's key for a top-level one (`radius`). */
  path: string;
  label: string;
  before: string;
  after: string;
}

/** A field's name as a tenant reads it, from its path in the theme. */
export function themeFieldLabel(path: string): string {
  const [group, field] = path.split(".");
  const choice = THEME_CHOICES.find((entry) => entry.group === group && entry.field === field);
  if (choice) return choice.label;
  if (group === "tokens") {
    const tokens: readonly { key: string; label: string }[] = STOREFRONT_TOKEN_GROUPS.flatMap(
      (entry): readonly { key: string; label: string }[] => entry.tokens,
    );
    return tokens.find((entry) => entry.key === field)?.label ?? path;
  }
  if (group === "rarity") {
    const tier = STOREFRONT_RARITY_TIERS.find((entry) => entry.key === field);
    return tier ? `${tier.label} rarity` : path;
  }
  if (group === "semantic") {
    const role = SEMANTIC_COLOR_GROUPS.flatMap((entry) => entry.roles).find(
      (entry) => entry.key === field,
    );
    return role?.label ?? path;
  }
  return TOP_LEVEL_LABELS[path] ?? path;
}

const TOP_LEVEL_LABELS: Record<string, string> = {
  radius: "Corner radius",
  fontFamily: "Font",
  headingFontFamily: "Heading font",
  monoFontFamily: "Numbers font",
  "stage.colors": "Background effect colors",
};

/**
 * A stored leaf as the review shows it: an option's label, a list, or the value. Unset, it names
 * what the storefront draws instead ("Bold (default)"), or "Default" where there is no one value.
 */
function leafText(path: string, json: string | undefined, theme: StorefrontTheme): string {
  const [group, field] = path.split(".");
  const choice = THEME_CHOICES.find((entry) => entry.group === group && entry.field === field);
  const value: unknown = json === undefined ? undefined : JSON.parse(json);
  if (value === undefined) {
    if (choice) {
      const drawn = choice.options.find((entry) => entry.value === choiceValue(theme, choice));
      if (drawn) return `${drawn.label} (default)`;
    }
    if (path === "radius") return `${DEFAULT_RADIUS_PX}px (default)`;
    return "Default";
  }
  const option = choice?.options.find((entry) => entry.value === value);
  if (option) return option.label;
  if (Array.isArray(value)) return value.join(", ");
  return String(value);
}

/**
 * Every field a save of `draft` would change against the stored theme, in the theme's order,
 * compared as both would be saved (the same rule as `countThemeChanges`, so the two agree).
 */
export function listThemeChanges(stored: StorefrontTheme, draft: StorefrontTheme): ThemeChange[] {
  const before = themeLeaves(toSavedTheme(stored), "", new Map());
  const after = themeLeaves(toSavedTheme(draft), "", new Map());
  return [...new Set([...before.keys(), ...after.keys()])]
    .filter((path) => before.get(path) !== after.get(path))
    .map((path) => ({
      path,
      label: themeFieldLabel(path),
      before: leafText(path, before.get(path), stored),
      after: leafText(path, after.get(path), draft),
    }));
}

/** `draft` with the field at `path` put back to its stored value (or unset, if it had none). */
export function withFieldReverted(
  draft: StorefrontTheme,
  stored: StorefrontTheme,
  path: string,
): StorefrontTheme {
  const [group, field] = path.split(".") as [keyof StorefrontTheme, string | undefined];
  if (field === undefined) return { ...draft, [group]: stored[group] };
  const storedGroup = (stored[group] ?? {}) as Record<string, unknown>;
  const draftGroup = { ...((draft[group] ?? {}) as Record<string, unknown>) };
  if (field in storedGroup) draftGroup[field] = storedGroup[field];
  else delete draftGroup[field];
  return { ...draft, [group]: draftGroup };
}

/** Every field a Style choice sets or draws under Details, by its path in the review. */
export function stylePaths(style: ThemeStyle): string[] {
  return [
    ...new Set([
      ...style.fields.map((field) => `${field.group}.${field.field}`),
      ...style.presets.flatMap((preset) => Object.keys(preset.values)),
      ...(style.radius ? ["radius"] : []),
      ...(style.stageColors ? ["stage.colors"] : []),
    ]),
  ];
}

/**
 * `draft` with a Style option picked. The option the stored theme matches puts every field the
 * choice covers back to its stored value, so trying another option and coming back is no change.
 * Any other is set through `withPreset`, except a backdrop effect, whose first pick starts its
 * colors from the palette so it draws something (`withStageRecipe`).
 */
export function withStylePicked(
  draft: StorefrontTheme,
  stored: StorefrontTheme,
  style: ThemeStyle,
  preset: ThemeStylePreset,
  palette: { primary: string; "chart-2": string },
): StorefrontTheme {
  if (matchedPreset(stored, style)?.label === preset.label) {
    return stylePaths(style).reduce((next, path) => withFieldReverted(next, stored, path), draft);
  }
  const recipe = preset.values["stage.recipe"] as (typeof STAGE_RECIPES)[number] | undefined;
  return recipe ? withStageRecipe(draft, recipe, palette) : withPreset(draft, preset);
}

/**
 * The theme fields the editor draws outside the choices: the palette and its raw tokens, the
 * fonts and the rarity colors, the worked-out color overrides, the background effect's colors
 * and the radius under Corners. With the choices, the whole theme.
 */
export const THEME_FIELDS_OUTSIDE_CHOICES = [
  "tokens",
  "rarity",
  "radius",
  "fontFamily",
  "headingFontFamily",
  "monoFontFamily",
  "semantic",
  "stage.colors",
] as const;

/** One semantic role as the Advanced section lists it. */
export interface SemanticColorField {
  key: SemanticColorKey;
  label: string;
}

/** A labeled group of semantic roles under Advanced. */
export interface SemanticColorGroup {
  label: string;
  description: string;
  /** Unset, the roles derive from the palette; else they take the design system's own default. */
  unset: "derived" | "default";
  roles: readonly SemanticColorField[];
}

/**
 * The semantic roles a theme may set (`SEMANTIC_COLOR_KEYS`), grouped for the Advanced section.
 * Each one appears once (a test holds it). Every role works unset, so these are overrides only.
 */
export const SEMANTIC_COLOR_GROUPS: readonly SemanticColorGroup[] = [
  {
    label: "Surfaces and borders",
    description: "Sunken and pressed backgrounds, and the stronger border weights.",
    unset: "derived",
    roles: [
      { key: "bg-inset", label: "Inset background" },
      { key: "bg-pressed", label: "Pressed background" },
      { key: "border-default", label: "Border" },
      { key: "border-strong", label: "Strong border" },
      { key: "border-input", label: "Input border" },
    ],
  },
  {
    label: "Text",
    description: "The two quietest text weights.",
    unset: "derived",
    roles: [
      { key: "text-tertiary", label: "Tertiary text" },
      { key: "text-disabled", label: "Disabled text" },
    ],
  },
  {
    label: "Accent",
    description: "The primary color's hover, pressed and tinted variations.",
    unset: "derived",
    roles: [
      { key: "accent-hover", label: "Accent hover" },
      { key: "accent-pressed", label: "Accent pressed" },
      { key: "accent-subtle", label: "Accent tint" },
      { key: "accent-text", label: "Accent text" },
      { key: "accent-highlight", label: "Accent highlight" },
    ],
  },
  {
    label: "Interaction",
    description: "The overlays a hovered or pressed row, tile or menu item takes.",
    unset: "derived",
    roles: [
      { key: "state-hover", label: "Hover overlay" },
      { key: "state-pressed", label: "Pressed overlay" },
    ],
  },
  {
    label: "Glass",
    description: "The fill and edge of glass panels, used when Glass is on.",
    unset: "derived",
    roles: [
      { key: "glass-fill", label: "Glass fill" },
      { key: "glass-edge", label: "Glass edge" },
      { key: "glass-active", label: "Glass active" },
    ],
  },
  {
    label: "Brand gradient",
    description:
      "The two ends of the gradient on highlights and glows. Unset, both are the primary.",
    unset: "derived",
    roles: [
      { key: "brand-start", label: "Gradient start" },
      { key: "brand-end", label: "Gradient end" },
    ],
  },
  {
    label: "Status",
    description: "Success and warning colors. Danger is the Destructive variable.",
    unset: "default",
    roles: [
      { key: "status-success", label: "Success" },
      { key: "status-warning", label: "Warning" },
    ],
  },
  {
    label: "Status tints",
    description: "The faint fills behind status messages, each derived from its status color.",
    unset: "derived",
    roles: [
      { key: "status-success-subtle", label: "Success tint" },
      { key: "status-warning-subtle", label: "Warning tint" },
      { key: "status-danger-subtle", label: "Danger tint" },
    ],
  },
  {
    label: "Chase tier",
    description: "The tier above Legendary that some stores draw.",
    unset: "default",
    roles: [{ key: "rarity-chase", label: "Chase" }],
  },
];

/** How many semantic roles a theme overrides, for the Advanced summary. */
export function countSemanticOverrides(theme: StorefrontTheme): number {
  return SEMANTIC_COLOR_KEYS.filter((key) => (theme.semantic?.[key] ?? "").trim() !== "").length;
}

/** The value a choice holds in a theme: the stored pick, else what the storefront renders. */
export function choiceValue(theme: StorefrontTheme, choice: ThemeChoice): string | number {
  // Resolved, not stored-or-default: an unset field can follow another (icon animation follows
  // the motion preset), and the editor shows what the store gets.
  const group = resolveThemeSpec(theme)[choice.group] as Record<string, string | number>;
  return group[choice.field] as string | number;
}

/** `theme` with one choice set; every other field, of that group and the rest, kept. */
export function withChoice(
  theme: StorefrontTheme,
  choice: ThemeChoice,
  value: string | number,
): StorefrontTheme {
  const group = (theme[choice.group] ?? {}) as Record<string, unknown>;
  return { ...theme, [choice.group]: { ...group, [choice.field]: value } };
}

/**
 * `theme` with a background effect picked. An effect drawn in no colors draws nothing, so the
 * first pick of one with no stage colors yet starts from the palette's primary and its second
 * chart color. Not `secondary`, which many palettes use as a muted surface; the generator
 * always derives the chart colors as vivid hues apart from the primary.
 */
export function withStageRecipe(
  theme: StorefrontTheme,
  recipe: (typeof STAGE_RECIPES)[number],
  palette: { primary: string; "chart-2": string },
): StorefrontTheme {
  const colors = theme.stage?.colors ?? [];
  const seeded =
    recipe !== "none" && colors.every((color) => color.trim() === "")
      ? [palette.primary, palette["chart-2"]]
      : colors;
  return { ...theme, stage: { ...theme.stage, recipe, colors: seeded } };
}

/** `theme` with stage color `index` set; "" clears it. At most `MAX_STAGE_COLORS`. */
export function withStageColor(
  theme: StorefrontTheme,
  index: number,
  value: string,
): StorefrontTheme {
  const colors = Array.from(
    { length: MAX_STAGE_COLORS },
    (_, i) => (i === index ? value : theme.stage?.colors?.[i]) ?? "",
  );
  return { ...theme, stage: { ...theme.stage, colors } };
}

/** `theme` with a semantic role overridden; "" goes back to the derived color. */
export function withSemanticColor(
  theme: StorefrontTheme,
  key: SemanticColorKey,
  value: string,
): StorefrontTheme {
  return { ...theme, semantic: { ...theme.semantic, [key]: value } };
}

/**
 * `theme` with every semantic color override cleared, so each role derives from the palette again
 * (or takes its built-in default). Overrides are tuned to the palette they were set against, so
 * after a palette change they are usually what keeps a field or border on the old colors.
 */
export function withoutSemanticOverrides(theme: StorefrontTheme): StorefrontTheme {
  return { ...theme, semantic: {} };
}

/** A trimmed string, or undefined for a blank one, which means "unset" on every text field. */
function setText(value: string | undefined): string | undefined {
  const trimmed = value?.trim();
  return trimmed ? trimmed : undefined;
}

/** The semantic roles a theme may set, minus blanks; a token-backed role never goes back. */
function setSemanticOverrides(
  semantic: StorefrontTheme["semantic"],
): Partial<Record<SemanticColorKey, string>> {
  const overrides: Partial<Record<SemanticColorKey, string>> = {};
  for (const key of SEMANTIC_COLOR_KEYS) {
    const value = setText(semantic?.[key]);
    if (value) overrides[key] = value;
  }
  return overrides;
}

/** `value` unless it is an object with nothing set, which is stored as absent. */
function unlessEmpty<T extends object>(value: T | undefined): T | undefined {
  if (!value) return undefined;
  return Object.values(value).some((entry) => entry !== undefined) ? value : undefined;
}

/**
 * The theme the editor saves, built from its draft. The draft starts as the stored theme and the
 * column is written whole, so everything comes back: a group the editor never touched is the
 * stored one, unchanged (a theme written by `dev:theme`, or by a later editor). Blank text fields
 * are dropped (blank means "unset"), as are rarity tiers the contract no longer defines and
 * semantic roles that belong to a token, which the save check would refuse.
 */
export function toSavedTheme(draft: StorefrontTheme): StorefrontTheme {
  const stageColors = (draft.stage?.colors ?? [])
    .map((color) => color.trim())
    .filter((color) => color !== "");
  const saved: StorefrontTheme = {
    ...draft,
    tokens: setTokenOverrides(draft.tokens ?? {}),
    rarity: setRarityOverrides(draft.rarity ?? {}),
    radius: setText(draft.radius),
    fontFamily: setText(draft.fontFamily),
    headingFontFamily: setText(draft.headingFontFamily),
    monoFontFamily: setText(draft.monoFontFamily),
    semantic: unlessEmpty(setSemanticOverrides(draft.semantic)),
    stage: unlessEmpty({
      ...draft.stage,
      colors: stageColors.length > 0 ? stageColors : undefined,
    }),
  };
  return Object.fromEntries(
    Object.entries(saved).filter(([, value]) => value !== undefined),
  ) as StorefrontTheme;
}

/** A theme as the Theme tab exports it: what a save would store, as indented JSON. */
export function themeToJson(theme: StorefrontTheme): string {
  return `${JSON.stringify(toSavedTheme(theme), null, 2)}\n`;
}

/** Why pasted theme JSON can't be loaded, worded for the field under the paste box. */
export type ThemeJsonResult =
  | { ok: true; theme: StorefrontTheme }
  | { ok: false; reason: "not_json" | "not_object" | "invalid"; message: string };

/**
 * Pasted theme JSON (an export from this or another environment, or a reference theme)
 * as a theme the editor can load, checked by the same schema a save is. A refusal names the one
 * thing it hit: not JSON at all, not an object, or the first field the save would refuse.
 */
export function parseThemeJson(text: string): ThemeJsonResult {
  let value: unknown;
  try {
    value = JSON.parse(text);
  } catch {
    return { ok: false, reason: "not_json", message: "This isn't valid JSON." };
  }
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    return {
      ok: false,
      reason: "not_object",
      message: "A theme is a JSON object, like the one Copy JSON gives you.",
    };
  }
  const parsed = lootThemeInputSchema.safeParse(value);
  if (!parsed.success) {
    const issue = parsed.error.issues[0];
    const where = issue?.path.length ? `${issue.path.join(".")}: ` : "";
    return { ok: false, reason: "invalid", message: `${where}${issue?.message ?? "not a theme"}` };
  }
  return { ok: true, theme: parsed.data };
}
